import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCompanyHolidays } from "@/hooks/use-company-holidays";
import { useInviteBodyTemplate } from "@/hooks/use-crm-config";
import { useBookMeeting, useFreeBusy, type BookMeetingResult } from "@/hooks/use-meetings";
import { buildInviteMessage, defaultMeetingTitle, messageProblem } from "@/lib/meeting-invite";
import { hostsAvailableAt, initialWeekMonday, weekRange, type WeekCell } from "@/lib/meeting-slots";
import { describeReminderPlan } from "@/lib/meeting-reminder-summary";
import { reminderPlan } from "@/lib/meeting-reminders";
import { isSmsCapable } from "@/lib/phone-format";
import { osloParts } from "@/lib/send-window";
import type { MeetingLocationType } from "@/lib/db-types";
import ContactFormDialog from "@/components/customers/ContactFormDialog";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import BookMeetingBasicsFields, { type BookContact } from "./BookMeetingBasicsFields";

export type { BookContact };
import BookMeetingHostPicker from "./BookMeetingHostPicker";
import BookMeetingWeekCalendar from "./BookMeetingWeekCalendar";

export interface BookSubject {
  kind: "customer" | "lead";
  id: string;
  name: string;
}

interface BookMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: BookSubject;
  contacts: BookContact[];
  opportunityId?: string | null;
  defaultContactId?: string | null;
  defaultTopic?: string | null;
  /** Internt notat ved åpning (f.eks. notatet fra samtalen der møtet ble avtalt). */
  defaultInternalNote?: string | null;
  /** Kalles før dialogen lukkes, med tidspunkt og tittel på møtet som ble booket. */
  onBooked?: (result: BookMeetingResult, booked: { start: string; title: string }) => void;
}

const LONG_DAY = new Intl.DateTimeFormat("nb-NO", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Oslo" });
const TIME = new Intl.DateTimeFormat("nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });
const todayKey = (now: Date) => {
  const p = osloParts(now);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
};

/**
 * Book kundemøte (CRM-4.4). Ukekalenderen (VIB-2987) viser når vertene er ledige, alle overlagt
 * eller én vert; et klikk på en ledig rute velger tiden. Bookeren er ikke selv med i møtet;
 * invitasjonen går fra vertens kalender, og påminnelsene planlegges automatisk.
 */
export default function BookMeetingDialog({
  open,
  onOpenChange,
  subject,
  contacts,
  opportunityId,
  defaultContactId,
  defaultTopic,
  defaultInternalNote,
  onBooked,
}: BookMeetingDialogProps) {
  const [now, setNow] = useState(() => new Date());
  const [contactId, setContactId] = useState("");
  const [title, setTitle] = useState("");
  const [locationType, setLocationType] = useState<MeetingLocationType>("meet");
  const [address, setAddress] = useState("");
  const [duration, setDuration] = useState(30);
  const [monday, setMonday] = useState(() => initialWeekMonday(new Date()));
  /** Vert som filter i kalenderen; null er alle verter. */
  const [filterHostId, setFilterHostId] = useState<string | null>(null);
  const [hostId, setHostId] = useState<string | null>(null);
  const [slot, setSlot] = useState<Date | null>(null);
  const [message, setMessage] = useState("");
  const [messageEdited, setMessageEdited] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  /** Kontakter lagt til eller rettet i dialogen; vises med en gang, før listen fra serveren er oppdatert. */
  const [savedContacts, setSavedContacts] = useState<BookContact[]>([]);
  const [contactForm, setContactForm] = useState<"new" | BookContact | null>(null);
  const { activeUser } = useImpersonation();
  // Kundekontakter kan bare ansatte skrive (RLS). Lead-kontakter går via RPC-en save_lead_contact,
  // som også slipper til møtebookeren på leads den har låst.
  const canEditContacts = subject.kind === "lead" || activeUser?.role !== "motebooker";
  const allContacts = useMemo(
    () => [...contacts.filter((c) => !savedContacts.some((s) => s.id === c.id)), ...savedContacts],
    [contacts, savedContacts],
  );

  const book = useBookMeeting();
  const { data: template } = useInviteBodyTemplate();
  const year = now.getFullYear();
  const { data: holidaysThis } = useCompanyHolidays(year);
  const { data: holidaysNext } = useCompanyHolidays(year + 1);
  const holidays = useMemo(() => new Set<string>([...(holidaysThis ?? []), ...(holidaysNext ?? [])]), [holidaysThis, holidaysNext]);

  useEffect(() => {
    if (!open) return;
    const n = new Date();
    setNow(n);
    const firstContact = contacts.find((c) => c.id === defaultContactId) ?? contacts[0];
    setContactId(firstContact?.id ?? "");
    setTitle(defaultMeetingTitle(subject.name, defaultTopic));
    setLocationType("meet");
    setAddress("");
    setDuration(30);
    setMonday(initialWeekMonday(n));
    setFilterHostId(null);
    setHostId(null);
    setSlot(null);
    setMessageEdited(false);
    setInternalNote(defaultInternalNote ?? "");
    setSavedContacts([]);
    // Åpning er det eneste som skal nullstille skjemaet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const contact = allContacts.find((c) => c.id === contactId) ?? null;

  const start = slot;
  const end = useMemo(() => (start ? new Date(start.getTime() + duration * 60_000) : null), [start, duration]);
  const range = useMemo(() => (open ? weekRange(monday) : null), [open, monday]);
  const { data: hosts = [], isLoading: hostsLoading, error: hostsError } = useFreeBusy(range);
  const notBefore = useMemo(() => new Date(now.getTime() + 15 * 60_000), [now]);
  const minMonday = useMemo(() => initialWeekMonday(now), [now]);

  const availability = useMemo(() => (start && end ? hostsAvailableAt(hosts, start, end) : []), [hosts, start, end]);
  const isFree = (id: string | null) => !!id && availability.some((a) => a.host.user_id === id && a.status === "free");

  const host = hosts.find((h) => h.user_id === hostId) ?? null;
  const hostOk = isFree(hostId);

  const selectCell = (cell: WeekCell) => {
    setSlot(cell.start);
    if (filterHostId) setHostId(filterHostId);
    else if (!cell.freeHosts.some((h) => h.user_id === hostId)) setHostId(cell.freeHosts.length === 1 ? cell.freeHosts[0].user_id : null);
  };
  const filterHost = (id: string | null) => {
    setFilterHostId(id);
    if (!id) return;
    setHostId(id);
    if (start && !isFree(id)) setSlot(null);
  };
  const changeWeek = (m: string) => {
    setMonday(m);
    setSlot(null);
    if (!filterHostId) setHostId(null);
  };

  // Forhåndsutfylt melding følger kontakt og vert til brukeren selv har skrevet.
  useEffect(() => {
    if (!template || messageEdited) return;
    setMessage(buildInviteMessage(template, { contactName: contact?.full_name ?? null, hostName: host?.full_name ?? null }));
  }, [template, contact?.full_name, host?.full_name, messageEdited]);

  const plan = useMemo(
    () =>
      start
        ? reminderPlan(
            { start, now, hasMobile: isSmsCapable(contact?.phone), hasEmail: !!contact?.email },
            { startHour: 8, holidays, tz: "Europe/Oslo" },
          )
        : null,
    [start, now, contact?.phone, contact?.email, holidays],
  );

  const problems: string[] = [];
  if (!contact) problems.push("Velg en kontaktperson");
  else if (!contact.email) problems.push("Kontaktpersonen mangler e-post, så invitasjonen kan ikke sendes");
  if (!title.trim()) problems.push("Tittel mangler");
  if (locationType === "physical" && !address.trim()) problems.push("Fysisk møte krever adresse");
  const msgProblem = messageProblem(message);
  if (msgProblem) problems.push(msgProblem);
  const canSubmit = problems.length === 0 && !!start && !!end && !!hostId && hostOk && !book.isPending;

  const submit = () => {
    if (!start || !end || !hostId || !contact) return;
    book.mutate(
      {
        hostUserId: hostId,
        contactId: contact.id,
        customerId: subject.kind === "customer" ? subject.id : null,
        leadId: subject.kind === "lead" ? subject.id : null,
        opportunityId: opportunityId ?? null,
        start: start.toISOString(),
        end: end.toISOString(),
        title: title.trim(),
        message: message.trim() || null,
        locationType,
        address: locationType === "physical" ? address.trim() : null,
        internalNote: internalNote.trim() || null,
      },
      {
        onSuccess: (result) => {
          toast.success("Møtet er booket", result.html_link ? { action: { label: "Åpne i kalenderen", onClick: () => window.open(result.html_link!, "_blank", "noreferrer") } } : undefined);
          onBooked?.(result, { start: start.toISOString(), title: title.trim() });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Book møte med {subject.name}</DialogTitle>
          <DialogDescription>Verten får hendelsen i kalenderen sin, og kunden får invitasjonen fra Google. Du er ikke selv med i møtet.</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) submit();
          }}
          className="space-y-6"
        >
          <BookMeetingBasicsFields
            contacts={allContacts}
            onNewContact={canEditContacts ? () => setContactForm("new") : undefined}
            onEditContact={canEditContacts && contact ? () => setContactForm(contact) : undefined}
            contact={contact}
            contactId={contactId}
            onContact={(v) => { setContactId(v); setMessageEdited(false); }}
            title={title}
            onTitle={setTitle}
            locationType={locationType}
            onLocationType={setLocationType}
            address={address}
            onAddress={setAddress}
            duration={duration}
            onDuration={(d) => { setDuration(d); setSlot(null); }}
          />

          <section aria-label="Når og hvem fra Sølvrev" className="space-y-3">
            <h3 className="text-sm font-medium">Når og hvem fra Sølvrev</h3>
            {hostsError ? (
              <p className="text-sm text-destructive">{(hostsError as Error).message}</p>
            ) : !hostsLoading && hosts.length === 0 ? (
              <BookMeetingHostPicker hosts={[]} selectedId={null} onSelect={setHostId} />
            ) : (
              <BookMeetingWeekCalendar
                hosts={hosts}
                loading={hostsLoading}
                monday={monday}
                onMonday={changeWeek}
                minMonday={minMonday}
                todayKey={todayKey(now)}
                filterHostId={filterHostId}
                onFilterHost={filterHost}
                durationMinutes={duration}
                holidays={holidays}
                notBefore={notBefore}
                selected={slot}
                onSelect={selectCell}
              />
            )}
            {start && end ? (
              <>
                <p className="border-[1.5px] border-foreground px-3 py-2 text-sm" aria-live="polite">
                  Valgt: <span className="font-medium">{LONG_DAY.format(start)} {TIME.format(start)} til {TIME.format(end)}</span>
                  {filterHostId && host && <> med <span className="font-medium">{host.full_name}</span></>}
                </p>
                {!filterHostId && <BookMeetingHostPicker hosts={availability} selectedId={hostId} onSelect={setHostId} />}
              </>
            ) : (
              !hostsLoading && !hostsError && hosts.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {hosts.every((h) => h.error) ? "Ingen av vertene har kalendertilgang, så ingen tider kan velges." : "Klikk en grønn rute for å velge tid."}
                </p>
              )
            )}
          </section>

          <section aria-label="Melding" className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="book-message">Melding til kunden</Label>
              <Textarea id="book-message" rows={6} value={message} onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }} />
              <p className="text-xs text-muted-foreground">Legges i Googles kalenderinvitasjon. Ikke nevn transkripsjon.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="book-note">Intern merknad til verten (valgfri)</Label>
              <Textarea id="book-note" rows={2} value={internalNote} onChange={(e) => setInternalNote(e.target.value)} />
            </div>
          </section>

          {plan && <p className="text-sm text-muted-foreground" aria-live="polite">{describeReminderPlan(plan)}</p>}
          {problems.length > 0 && start && (
            <ul className="text-sm text-destructive" aria-label="Mangler">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {book.isPending ? "Booker…" : "Book møte"}
            </Button>
          </DialogFooter>
        </form>
        {/* Utenfor bookingskjemaet: submit fra kontaktskjemaet skal ikke boble til bookingen. */}
        <ContactFormDialog
          open={contactForm !== null}
          onOpenChange={(o) => !o && setContactForm(null)}
          customerId={subject.kind === "customer" ? subject.id : null}
          leadId={subject.kind === "lead" ? subject.id : null}
          contact={contactForm === "new" ? null : contactForm}
          onSaved={(c) => {
            setSavedContacts((list) => [...list.filter((x) => x.id !== c.id), c]);
            setContactId(c.id);
            setMessageEdited(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
